import React, { useState } from "react";
import AsyncSelect from "react-select/async";
import { useFormik } from "formik";
import * as Yup from "yup";
import Modal from "react-bootstrap/Modal";
import Swal from "sweetalert2";
import { useSelector } from "react-redux";

import pharmacySoftwareService from "../../services/pharmacy-software.service";
import SearchService from "../../services/search.service";
import "./index.scss";

/**
 * Pioneer connection details for one pharmacy branch. The key and shared secret are write-only:
 * the API returns previews rather than the stored values, so on edit these fields start empty and
 * an empty field means "keep what is stored".
 *
 * `isOwnBranchOnly` is set for a pharmacy administrator: there is no branch to choose because the
 * API always applies their own, so the picker is replaced with their pharmacy name.
 */
const EditPharmacySoftwareModal = (props) => {
  const currentUser = useSelector((state) => state.currentUser);
  const [isSaving, setIsSaving] = useState(false);
  const isEdit = Boolean(props.record?.Id);
  const isOwnBranchOnly = Boolean(props.isOwnBranchOnly);
  // The branch is fixed either because the connection already exists or because the user only
  // administers one pharmacy.
  const isBranchLocked = isEdit || isOwnBranchOnly;

  const loadBranches = async (inputValue, callback) => {
    try {
      const branchId =
        currentUser?.user?.username === "admin"
          ? "admin"
          : currentUser?.user?.pharmacyBranchId;
      const data = await SearchService.Search(
        "pharmacies",
        inputValue,
        50,
        1,
        "id",
        "asc",
        false,
        branchId
      );
      callback(
        (data?.data || []).map((item) => ({
          id: item.id,
          label: item.ncpdp ? `${item.name} (NCPDP ${item.ncpdp})` : item.name,
        }))
      );
    } catch (error) {
      console.error("Error loading pharmacies:", error);
      callback([]);
    }
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      branch: props.record
        ? {
            id: props.record.PharmacyBranchId,
            label: props.record.PharmacyBranchName || `Pharmacy #${props.record.PharmacyBranchId}`,
          }
        : isOwnBranchOnly
        ? {
            id: currentUser?.user?.pharmacyBranchId,
            label:
              currentUser?.user?.pharmacyBranchName ||
              currentUser?.user?.pharmacyName ||
              "My pharmacy",
          }
        : null,
      softwareType: props.record?.SoftwareType || "pioneer",
      host: props.record?.Host || "",
      apiKey: "",
      sharedSecret: "",
      rejectUnauthorized: props.record ? props.record.RejectUnauthorized : true,
      requestedByEmployeeId: props.record?.RequestedByEmployeeId || "",
      defaultSendMode: props.record?.DefaultSendMode || "on_hold",
      isActive: props.record ? props.record.IsActive : true,
    },
    validationSchema: Yup.object({
      branch: isBranchLocked
        ? Yup.mixed().nullable()
        : Yup.mixed().required("Choose a pharmacy"),
      host: Yup.string().required("Field is required"),
      requestedByEmployeeId: Yup.string().required("Field is required"),
      apiKey: isEdit
        ? Yup.string()
        : Yup.string().required("Field is required"),
      sharedSecret: isEdit
        ? Yup.string()
        : Yup.string().required("Field is required"),
    }),
    onSubmit: async (values) => {
      const payload = {
        host: values.host.trim(),
        requestedByEmployeeId: values.requestedByEmployeeId.trim(),
        rejectUnauthorized: Boolean(values.rejectUnauthorized),
        defaultSendMode: values.defaultSendMode,
        isActive: Boolean(values.isActive),
      };
      if (values.apiKey.trim()) payload.apiKey = values.apiKey.trim();
      if (values.sharedSecret.trim()) payload.sharedSecret = values.sharedSecret.trim();

      setIsSaving(true);
      const result = isEdit
        ? await pharmacySoftwareService.UpdateConnection(props.record.Id, payload)
        : await pharmacySoftwareService.CreateConnection({
            ...payload,
            pharmacyBranchId: values.branch?.id,
            softwareType: values.softwareType,
          });
      setIsSaving(false);

      if (result.error) {
        Swal.fire({
          icon: "error",
          title: "Could not save the connection",
          text: result.message || "Please review the details and try again.",
        });
        return;
      }

      formik.resetForm();
      props.onHide(true);
    },
  });

  const close = () => {
    formik.resetForm();
    props.onHide(false);
  };

  const fieldError = (name) =>
    formik.touched[name] && formik.errors[name] ? (
      <small className="text-danger">{formik.errors[name]}</small>
    ) : null;

  return (
    <Modal
      onHide={close}
      show={props.show}
      aria-labelledby="pharmacy-software-modal-title"
      centered
      backdrop="static"
      keyboard={false}
      scrollable
      dialogClassName="modal-lg"
    >
      <Modal.Header closeButton>
        <Modal.Title id="pharmacy-software-modal-title">
          {isEdit
            ? `Edit ${props.record.SoftwareLabel} connection`
            : "Connect a pharmacy to software"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <form onSubmit={formik.handleSubmit} className="pharmacy-software-form">
          <div className="row">
            <div className="col-md-6 form-group">
              <label htmlFor="branch-ddl" className="form-label">
                Pharmacy
              </label>
              <i className="fa fa-asterisk"></i>
              {isBranchLocked ? (
                <input
                  id="branch-ddl"
                  className="form-control"
                  value={formik.values.branch?.label || ""}
                  disabled
                />
              ) : (
                <AsyncSelect
                  id="branch-ddl"
                  name="branch"
                  classNamePrefix="react-select"
                  getOptionValue={(option) => option.id}
                  menuPlacement="auto"
                  isClearable
                  defaultOptions
                  value={formik.values.branch}
                  onBlur={formik.handleBlur}
                  loadOptions={loadBranches}
                  onChange={(item) => formik.setFieldValue("branch", item)}
                />
              )}
              {isEdit ? (
                <small className="text-muted">
                  A connection stays with the pharmacy it was created for.
                </small>
              ) : isOwnBranchOnly ? (
                <small className="text-muted">
                  You can only connect the pharmacy you administer.
                </small>
              ) : (
                fieldError("branch")
              )}
            </div>

            <div className="col-md-6 form-group">
              <label htmlFor="softwareType-input" className="form-label">
                Software
              </label>
              <select
                id="softwareType-input"
                name="softwareType"
                className="form-select"
                value={formik.values.softwareType}
                onChange={formik.handleChange}
                disabled={isEdit}
              >
                <option value="pioneer">PioneerRx</option>
              </select>
            </div>
          </div>

          <div className="row">
            <div className="col-md-8 form-group">
              <label htmlFor="host-input" className="form-label">
                Host
              </label>
              <i className="fa fa-asterisk"></i>
              <input
                id="host-input"
                name="host"
                value={formik.values.host}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                type="text"
                className="form-control"
                placeholder="hostname:port"
              />
              {fieldError("host")}
            </div>

            <div className="col-md-4 form-group">
              <label htmlFor="requestedByEmployeeId-input" className="form-label">
                Employee ID
              </label>
              <i className="fa fa-asterisk"></i>
              <input
                id="requestedByEmployeeId-input"
                name="requestedByEmployeeId"
                value={formik.values.requestedByEmployeeId}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                type="text"
                className="form-control"
                placeholder="employee-id"
              />
              {fieldError("requestedByEmployeeId")}
            </div>
          </div>

          <div className="row">
            <div className="col-md-6 form-group">
              <label htmlFor="apiKey-input" className="form-label">
                API key
              </label>
              {isEdit ? null : <i className="fa fa-asterisk"></i>}
              <input
                id="apiKey-input"
                name="apiKey"
                value={formik.values.apiKey}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                type="password"
                autoComplete="new-password"
                className="form-control"
                placeholder={
                  isEdit
                    ? `Stored: ${props.record?.ApiKeyPreview || "•••"} — leave blank to keep`
                    : ""
                }
              />
              {fieldError("apiKey")}
            </div>

            <div className="col-md-6 form-group">
              <label htmlFor="sharedSecret-input" className="form-label">
                Shared secret
              </label>
              {isEdit ? null : <i className="fa fa-asterisk"></i>}
              <input
                id="sharedSecret-input"
                name="sharedSecret"
                value={formik.values.sharedSecret}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                type="password"
                autoComplete="new-password"
                className="form-control"
                placeholder={
                  isEdit
                    ? `Stored: ${props.record?.SharedSecretPreview || "•••"} — leave blank to keep`
                    : ""
                }
              />
              {fieldError("sharedSecret")}
            </div>
          </div>

          <div className="row">
            <div className="col-md-6 form-group">
              <label htmlFor="defaultSendMode-input" className="form-label">
                Default send mode
              </label>
              <select
                id="defaultSendMode-input"
                name="defaultSendMode"
                className="form-select"
                value={formik.values.defaultSendMode}
                onChange={formik.handleChange}
              >
                <option value="on_hold">Add On Hold</option>
                <option value="intake">Intake</option>
              </select>
              <small className="text-muted">
                Preselected in the Software tab; a pharmacist can still switch per prescription.
              </small>
            </div>

            <div className="col-md-6 form-group">
              <div className="form-check mt-4">
                <input
                  id="isActive-input"
                  name="isActive"
                  className="form-check-input"
                  type="checkbox"
                  checked={Boolean(formik.values.isActive)}
                  onChange={formik.handleChange}
                />
                <label className="form-check-label" htmlFor="isActive-input">
                  Active
                </label>
              </div>
              <div className="form-check">
                <input
                  id="rejectUnauthorized-input"
                  name="rejectUnauthorized"
                  className="form-check-input"
                  type="checkbox"
                  checked={Boolean(formik.values.rejectUnauthorized)}
                  onChange={formik.handleChange}
                />
                <label className="form-check-label" htmlFor="rejectUnauthorized-input">
                  Verify the TLS certificate
                </label>
                <div>
                  <small className="text-muted">
                    Uncheck only for a Pioneer host with a self-signed certificate.
                  </small>
                </div>
              </div>
            </div>
          </div>
        </form>
      </Modal.Body>
      <Modal.Footer>
        <button
          type="button"
          className="btn btn-default"
          onClick={formik.submitForm}
          disabled={isSaving}
        >
          {isSaving ? "Saving…" : "Save"}
        </button>
        <button type="button" className="btn btn-secondary" onClick={close}>
          Cancel
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default EditPharmacySoftwareModal;
